import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { PaymentType, PostedPaymentInfo } from '@cms-max/medicare-api-sdk'
import { Card } from '@cmsgov/ds-medicare-gov'
import { ReactComponent as CaretIcon } from 'images/Icon_Caret.svg'
import classNames from 'classnames'

import classes from './PostedPaymentCard.module.scss'
import { Button } from 'components/Button/Button'
import { DateUtil } from 'utils/date'
import { useEvent } from 'hooks/useEvent'

type PostedPaymentCardProps = {
  className?: string
  postedPayment: PostedPaymentInfo
}
export function PostedPaymentCard ({ className, postedPayment }: PostedPaymentCardProps) {
  const { t } = useTranslation('premiumsHistory')
  const { fireEvent } = useEvent('premiumsHistory')
  const [isOpen, setIsOpen] = useState(false)
  const handleClick = () => {
    fireEvent(isOpen ? 'premiums_paymenthistory_importantinfo_closedrawer' : 'premiums_paymenthistory_importantinfo_opendrawer', {
      date: postedPayment.postDate
    })
    setIsOpen((prev) => !prev)
  }

  const postDate = DateUtil.fromFormattedString(postedPayment.postDate, 'yyyy-MM-dd')
  const formattedDate = postDate.toServerDateString('MM/dd/yyyy')
  const paymentMethodLabel = postedPayment.adjustmentType ? t('postedPayments.adjustmentType') : t('postedPayments.paymentMethod')
  let importantPaymentInfo
  let paymentMethodValue

  const paymentMethodMappings = {
    ACH: t('paymentMethods.ach'),
    ONLINE_BANK: t('paymentMethods.onlineBank'),
    CHECK: t('paymentMethods.check'),
    CHECK_DELAYED: t('paymentMethods.checkDelayed'),
    CHECK_SSA_GENERATED: t('paymentMethods.checkSsaGenerated'),
    CREDIT_CARD: t('paymentMethods.creditCard'),
    CREDIT_OR_DEBIT_MEDICARE_GOV: t('paymentMethods.creditOrDebit')
  }

  if (postedPayment.adjustmentType) {
    paymentMethodValue = postedPayment.adjustmentType
  } else if (postedPayment.paymentMethodType === 'OTHER') {
    paymentMethodValue = postedPayment.paymentMethodDescription
  } else {
    paymentMethodValue = postedPayment.paymentMethodType ? paymentMethodMappings[postedPayment.paymentMethodType] : ''
  }

  if (postedPayment.paymentMethodType === PaymentType.CheckDelayed) {
    importantPaymentInfo = t('paymentMethods.checkDelayedDescription')
  } else if (postedPayment.paymentMethodType === PaymentType.CheckSsaGenerated) {
    importantPaymentInfo = t('paymentMethods.checkSsaGeneratedDescription')
  }

  const toggleButton = () => {
    return (
        <Button
          data-testid="card-toggle-button"
          className="ds-c-button ds-c-button--ghost my-u-px--0 my-u-py--0"
          onClick={handleClick}
          aria-expanded={isOpen}
          aria-label={isOpen ? 'Close details' : 'Open details' }
        >
          {t('postedPayments.importantInfo')}
          <CaretIcon
            className={`my-c-caret--${isOpen ? 'up' : 'down'} my-c-caret--blue my-u-ml--6`}
            aria-hidden
            focusable={false}
          />
        </Button>
    )
  }

  return (
    <Card className={classNames('ds-u-padding--3', className)}>
      <div className='ds-l-row' data-testid='posted-payment-card'>
        <div className='ds-l-col--12 ds-l-lg-col--6'>
          <dl className='ds-u-margin--0'>
            <dt>{t('postedPayments.postDate')}</dt>
            <dd className='ds-u-margin--0 ds-u-margin-bottom--3 ds-text-body--lg ds-u-font-weight--semibold'>{formattedDate}</dd>

            <dt>{paymentMethodValue && paymentMethodLabel}</dt>
            <dd className='ds-u-margin--0 ds-text-body--lg'>{paymentMethodValue}</dd>
          </dl>
        </div>
        <div className='ds-l-col--12 ds-l-lg-col--6'>
          <table className={classNames('ds-u-text-align--left', classes.table)}>
            <tbody>
            <tr>
              <th>{t('postedPayments.appliedToPartB')}</th>
              <td>${postedPayment.appliedToPartB.toFixed(2)}</td>
            </tr>
            <tr>
              <th>{t('postedPayments.appliedToPartA')}</th>
              <td>${postedPayment.appliedToPartA.toFixed(2)}</td>
            </tr>
            <tr>
              <th>{t('postedPayments.appliedToPartDIRMAA')}</th>
              <td>${postedPayment.appliedToPartDIRMAA.toFixed(2)}</td>
            </tr>
            </tbody>
            <tfoot>
              <tr>
                <th>{t('postedPayments.total')}</th>
                <td>${postedPayment.total.toFixed(2)}</td>
              </tr>
              {(postedPayment.paymentMethodType === PaymentType.CheckDelayed || postedPayment.paymentMethodType === PaymentType.CheckSsaGenerated) &&
                <>
                  <tr>
                    <td colSpan={2} className={classes.info}>{toggleButton()}</td>
                  </tr>
                  {isOpen &&
                    <tr>
                      <td colSpan={2} className={classes.breakText}>
                      <Trans i18nKey={importantPaymentInfo} components={{
                        ul: <ul />,
                        li: <li />
                      }} />
                      </td>
                    </tr>
                  }
                </>
              }
            </tfoot>
          </table>
        </div>
      </div>
    </Card>
  )
}

export default PostedPaymentCard
